import { fx_crearBaseLocal, reqToPromise, txDone } from "../../../utils/idbUtils";

const DB_NAME = "grpc_lotes";
const DB_VERSION = 3;
const STORE_LOTES = "lotes";
const STORE_SYNC = "sync";

/** Item mínimo que necesita el merge de delta (mismo contrato que
 *  `GrpcCatalogoItem` en `GrpcCatalogosDatabase.ts`, pero acá el watermark es
 *  **por finca**, no uno solo global). */
export type GrpcLoteItem = {
  id: string;
  eliminado: boolean;
  ultimaActualizacion: bigint;
  fincaId: string;
};

/** IndexedDB **nueva y separada** para los lotes que trae `ClienteFincaLoteGrpcService`
 *  (ver CLAUDE.md del Server, sección migración a gRPC-Web) — a propósito, distinta
 *  de `GrpcFincaLoteDatabase` (`grpc_login_fincas`): esa guarda los lotes que trae el
 *  *login* (shape reducido, sin ganadería, sin delta, sin consumidores todavía — no
 *  se toca acá); ésta guarda los lotes reales que lee/edita la app, con delta real
 *  por `fincaId` (igual patrón que `GrpcCatalogosDatabase`, pero el watermark es por
 *  finca en vez de uno solo global: cada finca sincroniza independiente). */
class GrpcLoteDatabase {
  /** Conexión a la base local con el ayudante común, que al cambiar de
   *  versión conserva los datos (ver `fx_crearBaseLocal`). La versión 3
   *  (2026-09-30) se subió a pedido del usuario para una limpieza única al
   *  estrenar el ayudante: un navegador con una versión anterior vacía la
   *  base una sola vez y la vuelve a llenar desde el servidor. */
  private readonly _base = fx_crearBaseLocal(
    DB_NAME,
    DB_VERSION,
    [
      { nombre: STORE_LOTES, clave: "id", indices: ["fincaId"] },
      { nombre: STORE_SYNC, clave: "fincaId" },
    ],
    { vaciarAntesDe: 3 },
  );

  /** Conexión a la base local, abierta la primera vez y reusada después. */
  private open(): Promise<IDBDatabase> {
    return this._base.abrir();
  }

  /** Watermark de la última sincronización exitosa de una finca. `0n` si
   *  todavía no sincronizó nunca (trae el listado completo). */
  async getUltimaSincronizacionAsync(fincaId: string): Promise<bigint> {
    const db = await this.open();
    const store = db.transaction(STORE_SYNC, "readonly").objectStore(STORE_SYNC);
    const row = await reqToPromise<{ fincaId: string; ultimaSincronizacion: string } | undefined>(
      store.get(fincaId),
    );
    return row ? BigInt(row.ultimaSincronizacion) : 0n;
  }

  /** Aplica un delta de una finca: `put` de los lotes vivos, `delete` de los
   *  marcados `eliminado`, y guarda el nuevo watermark de esa finca — mismo
   *  criterio que `GrpcCatalogosDatabase.putDeltaAsync`. */
  async putDeltaAsync<T extends GrpcLoteItem>(
    fincaId: string,
    items: readonly T[],
    nuevoWatermark: bigint,
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction([STORE_LOTES, STORE_SYNC], "readwrite");
    const store = tx.objectStore(STORE_LOTES);
    const syncStore = tx.objectStore(STORE_SYNC);

    for (const item of items) {
      if (item.eliminado) {
        store.delete(item.id);
      } else {
        store.put(item);
      }
    }
    syncStore.put({ fincaId, ultimaSincronizacion: nuevoWatermark.toString() });

    await txDone(tx);
  }

  /** Borra el watermark de una finca: la próxima sincronización trae todos
   *  sus lotes (como la primera vez), no solo los cambios. Queda guardado en
   *  la base, así que vale aunque el usuario recargue el navegador antes. */
  async borrarSincronizacionAsync(fincaId: string): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_SYNC, "readwrite");
    tx.objectStore(STORE_SYNC).delete(fincaId);
    await txDone(tx);
  }

  /** Todos los lotes guardados de una finca, vía el índice `fincaId`. */
  async getListByFincaIdAsync<T>(fincaId: string): Promise<T[]> {
    const db = await this.open();
    const index = db
      .transaction(STORE_LOTES, "readonly")
      .objectStore(STORE_LOTES)
      .index("fincaId");
    return reqToPromise<T[]>(index.getAll(fincaId));
  }

  /** Actualiza (o inserta) un solo lote sin tocar el resto — lo llama el
   *  guardado puntual (`LoteGrpcSaveService`) para que los consumidores vean
   *  el cambio sin esperar al próximo delta. */
  async putOneAsync<T extends GrpcLoteItem>(item: T): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_LOTES, "readwrite");
    tx.objectStore(STORE_LOTES).put(item);
    await txDone(tx);
  }

  /** Actualiza (o inserta) varios lotes en una sola transacción, sin tocar el
   *  resto ni el watermark; los marcados `eliminado` se borran. Lo usa
   *  `fx_aplicarCambiosMovimientoAsync` con los lotes que el server recalculó
   *  por un movimiento de ganadería (2026-10-08): el mapa y el dashboard los
   *  leen al instante, sin volver a pedir el listado. */
  async putManyAsync<T extends GrpcLoteItem>(items: readonly T[]): Promise<void> {
    if (items.length === 0) return;
    const db = await this.open();
    const tx = db.transaction(STORE_LOTES, "readwrite");
    const store = tx.objectStore(STORE_LOTES);
    for (const item of items) {
      if (item.eliminado) {
        store.delete(item.id);
      } else {
        store.put(item);
      }
    }
    await txDone(tx);
  }
}

/** Singleton de la base de lotes por gRPC-Web (con delta por finca). */
export const grpcLoteDatabase = new GrpcLoteDatabase();
